// Belle Property detail-page parser. Belle's listing pages carry no listing
// JSON (no JSON-LD, no __NEXT_DATA__); everything is server-rendered markup
// between the hero and the location map:
//   <meta name="DC.Title" content="2/14 Bay Street, Mordialloc, VIC 3195">
//   <section class="section property-hero"> … <div class="status">SOLD</div>
//   <div class="price">$840,000</div>          ("Price undisclosed" when withheld)
//   <div class="feature bed" aria-label="bedrooms">2</div>   (bath, car likewise)
//   <div class="label">Property type</div><div class="value">Unit</div>
//   <div class="label">Land size</div> <div class="value">206.0 sqm</div>
//   <div class="name"><a href="…/mentone/robert-pullia">Robert Pullia</a></div>
//   <section class="section property-location" data-location-lat=… data-location-lng=…>
//
// Belle shows NO sold date on a sold listing (only a site-wide
// article:published_time), so saleDate is always null here. The meta
// description says "for sale" even on sold pages; the status badge is the
// listing status.

import {
  areaValue,
  count,
  decodeEntities,
  formatAddress,
  htmlText,
  propertyTypeLabel,
  readMeta,
  soldAmount,
  stateCode,
  text,
} from "../shared/listing-html.ts";

const ADDRESS_RE = /^(.+),\s*([^,]+),\s*([A-Z]{2,3})\s+(\d{4})$/i;

function listingBlock(html: string): string | null {
  const start = html.indexOf('<section class="section property-hero"');
  const location = html.indexOf('<section class="section property-location', start);
  if (start < 0 || location < 0) return null;
  const tagEnd = html.indexOf(">", location);
  return html.slice(start, tagEnd > location ? tagEnd + 1 : location);
}

function infoValue(block: string, label: string): string | null {
  const match = block.match(
    new RegExp(
      `<div class="label">\\s*${label}\\s*</div>\\s*<div class="value">([^<]*)</div>`,
      "i",
    ),
  );
  return match ? text(htmlText(match[1])) : null;
}

function feature(block: string, kind: string): number | null {
  const match = block.match(
    new RegExp(`<div class="feature ${kind}"[^>]*>\\s*(\\d+)\\s*</div>`, "i"),
  );
  return match ? count(match[1]) : null;
}

function officeName(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function parseBellePropertyDetailHtml(html: string, _url: string): Record<string, unknown> {
  const title = (readMeta(html, "DC.Title") ?? readMeta(html, "og:title"))?.replace(/\s+/g, " ");
  const address = title?.match(ADDRESS_RE);
  const block = listingBlock(html);
  // A challenge page or a page without the listing has no hero-to-map block
  // and no "<street>, <suburb>, <STATE> <postcode>" title: no data.
  if (!address || !block) return {};

  const suburb = address[2].trim();
  const state = stateCode(address[3]);
  const postcode = address[4];
  const hit: Record<string, unknown> = {
    address: formatAddress(address[1].trim(), suburb, state, postcode),
    suburb,
    state,
    postcode,
  };

  const status = block.match(/<div class="status[^"]*"[^>]*>\s*([^<]+?)\s*<\/div>/i)?.[1];
  const sold = /^sold$/i.test(status ?? "");
  hit.listingType = sold ? "sold" : null;
  hit.saleDate = null;

  const priceDisplay = block.match(/<div class="price">([^<]*)<\/div>/i);
  hit.priceDisplay = priceDisplay ? text(htmlText(priceDisplay[1])) : null;
  hit.salePrice = sold ? soldAmount(hit.priceDisplay as string | null) : null;
  hit.price = hit.salePrice;

  hit.bedrooms = feature(block, "bed");
  hit.bathrooms = feature(block, "bath");
  hit.parking = feature(block, "car");
  hit.propertyType = propertyTypeLabel(infoValue(block, "Property type"));

  const land = infoValue(block, "Land size")?.match(/^([\d,.]+)\s*(?:sqm|m2|m²)$/i);
  hit.landSize = land ? areaValue(land[1]) : null;
  hit.landSizeUnit = hit.landSize ? "sqm" : null;

  const lat = Number(block.match(/data-location-lat="(-?\d+(?:\.\d+)?)"/)?.[1]);
  const lng = Number(block.match(/data-location-lng="(-?\d+(?:\.\d+)?)"/)?.[1]);
  if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
    hit.latitude = lat;
    hit.longitude = lng;
  }

  // Agent links are /<office-slug>/<agent-slug>; the office slug names the
  // selling Belle Property office (e.g. mentone → "Belle Property Mentone").
  const agents: string[] = [];
  let office: string | null = null;
  for (const match of block.matchAll(/<div class="name"><a href="([^"]*)">([^<]+)<\/a><\/div>/gi)) {
    const name = decodeEntities(match[2]).trim();
    if (name && !agents.includes(name)) agents.push(name);
    office ??= match[1].match(/^(?:https?:\/\/[^/]+)?\/([a-z0-9-]+)\/[a-z0-9-]+\/?$/i)?.[1] ?? null;
  }
  hit.agentName = agents.length > 0 ? agents.join(", ") : null;
  hit.agencyName = office ? `Belle Property ${officeName(office)}` : "Belle Property";

  return hit;
}
